'use client';

import { useEffect, useMemo, useState } from 'react';
import { orderBy } from 'lodash';
import {
  DataTable,
  SortDirection,
  type DataTableColumn,
} from '@xtnd/web/shared/components/DataTable';
import { AppealStatus } from '@repo/types';

import { DEFAULT_APPEAL_STATUSES, WRITE_OFF_TABLE_DEFAULT_PROPS } from '../../constants';
import { ArWriteOffFilter, WriteOff, WriteOffItem, FlatWriteOffItem } from '../../types/writeOffs';
import { ClientInvoicesHeader } from './ClientInvoicesHeader';
import { ClientInvoiceToolbar } from './FilterToolbars';
import { useInvoiceContext } from '../common/InvoiceContext';

type Props = Readonly<{
  invoices: WriteOff[];
  clientName: string;
  columns: DataTableColumn<WriteOff>[];
  onBackToClient: () => void;
  isDataTableLoading: boolean;
  filters: ArWriteOffFilter;
  onFiltersChange: (filters: ArWriteOffFilter) => void;
  /** Optional: Override the default toolbar rendering */
  renderToolbar?: () => React.ReactNode;
  getRowClassName?: (row: WriteOff, index: number) => string;
  /** When true, flatten invoices by their write-off items (one row per write-off). Used in All tab. */
  shouldFlatten?: boolean;
}>;

const DEFAULT_PAGE_SIZE = 25;

export default function ClientInvoicesTable({
  invoices,
  clientName,
  columns,
  onBackToClient,
  isDataTableLoading,
  filters,
  onFiltersChange,
  renderToolbar,
  getRowClassName,
  shouldFlatten = false,
}: Props) {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);
  const [isLocalLoading, setIsLocalLoading] = useState(false);
  const [sorting, setSorting] = useState<Record<string, SortDirection>>({});
  const { openInvoiceDetailDrawer } = useInvoiceContext();

  // Remove the "Client" column since we're already viewing invoices for a specific client
  const filteredColumns = useMemo(() => {
    return columns.filter(column => column.key !== 'clientName');
  }, [columns]);

  // Flatten invoices by write-off items when shouldFlatten is true (for All tab)
  const flattenedInvoices = useMemo(() => {
    if (!shouldFlatten) {
      return invoices as unknown as FlatWriteOffItem[];
    }

    return invoices.flatMap(invoice => {
      const items = invoice.writeOffs;

      if (items?.length) {
        return items.map((item: WriteOffItem) => ({
          ...invoice,
          ...item,
          writeOffStatus: item.status,
          invoice: invoice,
          _parentInvoice: invoice,
          writeOffAmount: item.amount,
          writeOff: item,
        })) as unknown as FlatWriteOffItem[];
      }

      // Fallback for invoices without writeOffs array
      return [
        {
          ...invoice,
          invoice: invoice,
          category: undefined,
          type: undefined,
          amount: invoice.writeOffAmount,
          feesCut: invoice.totalFeesCut,
          expensesCut: invoice.totalExpensesCut,
        } as unknown as FlatWriteOffItem,
      ];
    });
  }, [invoices, shouldFlatten]);

  // Apply filters (category/appealStatus + invoiceNumber/matterName search) to invoices
  const filteredInvoices = useMemo(() => {
    let result = flattenedInvoices;

    // Filter by category (for ApprovedTab)
    if (filters.category) {
      result = result.filter(invoice => invoice.category === filters.category);
    }

    // Filter by appeal status (for other tabs)
    const appealStatuses = filters.appealStatus ?? DEFAULT_APPEAL_STATUSES;
    if (appealStatuses.length) {
      result = result.filter(invoice =>
        appealStatuses.includes((invoice as any).appealStatus as AppealStatus)
      );
    }

    // Filter by invoice number, matter number, or matter name (shared search input)
    const search = (filters.invoiceNumber || filters.matterDisplayNumber || '')
      .trim()
      .toLowerCase();
    if (search) {
      result = result.filter(invoice => {
        const invoiceNumber = invoice.invoiceNumber?.toLowerCase() ?? '';
        const matterDisplayNumber = invoice.matterDisplayNumber?.toLowerCase() ?? '';
        return invoiceNumber.includes(search) || matterDisplayNumber.includes(search);
      });
    }

    return result;
  }, [
    flattenedInvoices,
    filters.category,
    filters.appealStatus,
    filters.invoiceNumber,
    filters.matterDisplayNumber,
  ]);

  const sortedInvoices = useMemo(() => {
    let sorted = [...filteredInvoices];

    if (Object.keys(sorting).length > 0) {
      const mapSortKey: Record<string, string> = {
        total: 'total',
        amountPaid: 'amountPaid',
        outstandingBalance: 'outstandingBalance',
        writeOffAmount: 'writeOffAmount',
        daysPastDue: 'daysPastDue',
      };

      const [sortKey, order] = Object.entries(sorting)[0] || [];

      if (sortKey && mapSortKey[sortKey]) {
        sorted = orderBy(filteredInvoices, mapSortKey[sortKey], order);

        if (sortKey === 'amountPaid') {
          sorted = orderBy(
            filteredInvoices,
            inv => (inv.paid ?? 0) + (inv.creditsIssued ?? 0),
            order
          );
        }
      }
    }

    return sorted;
  }, [filteredInvoices, sorting]);

  // Apply pagination to filtered invoices
  const paginatedInvoices = useMemo(() => {
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    return sortedInvoices.slice(startIndex, endIndex);
  }, [sortedInvoices, page, limit]);

  // Format data using the common utility
  const tableData = useMemo(() => {
    return {
      data: paginatedInvoices,
      pagination: {
        total: filteredInvoices.length,
        page,
        limit,
        totalPages: Math.ceil(filteredInvoices.length / limit || 1),
      },
    };
  }, [paginatedInvoices, filteredInvoices.length, page, limit]);

  const handlePaginationChange = (pagination: { page: number; limit: number }) => {
    setPage(pagination.page);
    setLimit(pagination.limit);
  };

  // Reset to page 1 and show loading effect when filters change
  useEffect(() => {
    setPage(1);

    // Show loading effect for realistic user experience
    setIsLocalLoading(true);
    const timeoutId = setTimeout(() => {
      setIsLocalLoading(false);
    }, 400); // 400ms delay for realistic loading effect

    return () => {
      clearTimeout(timeoutId);
    };
  }, [filters.category, filters.appealStatus, filters.invoiceNumber, filters.matterDisplayNumber]);

  // Combine external loading state with local loading state
  const isLoading = isDataTableLoading || isLocalLoading;

  // Default toolbar renderer
  const defaultToolbar = () => (
    <ClientInvoiceToolbar
      isDataTableLoading={isDataTableLoading}
      filters={filters}
      onChange={onFiltersChange}
    />
  );

  return (
    <>
      {/* Client Invoices Header with Back Button and Toolbar */}
      <ClientInvoicesHeader
        clientName={clientName}
        onBackToClients={onBackToClient}
        renderToolbar={renderToolbar ?? defaultToolbar}
      />

      {/* Data Table */}
      <div className="mt-4">
        <DataTable
          {...WRITE_OFF_TABLE_DEFAULT_PROPS}
          columns={filteredColumns}
          data={{
            data: tableData.data,
            total: tableData.pagination.total,
          }}
          isLoading={isLoading}
          pagination={{ page, limit }}
          onPaginationChange={handlePaginationChange}
          emptyState={{
            icon: 'HiClipboardList',
            title: 'No invoices found',
            description: 'There are no invoices found matching the criteria.',
          }}
          onSort={sortBy => setSorting({ ...sortBy })}
          sorting={sorting}
          getRowKey={(_, index: number) => `invoice-row-${index}`}
          getRowClassName={getRowClassName}
          onRowClick={row => {
            const writeOffId = (row as any).writeOffId || row.writeOffs?.[0]?.writeOffId;
            openInvoiceDetailDrawer(row.invoiceId, {
              writeOffId,
              writeOffStatus: row.writeOffStatus,
            });
          }}
        />
      </div>
    </>
  );
}
